import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Formik, FormikHelpers, Form } from "formik";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { PhoneInput } from "@/core/components/phone-input";
import { Button } from "@volca/ui/components/ui/button";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { InfoWithLabel } from "@volca/ui/components/ui/info-with-label";
import { Item, ItemActions, ItemContent } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { ICustomer } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";

interface LeadDetailsFormProps {
  businessId: string;
  initialData: ICustomer | undefined;
}

interface CustomerFormValues {
  name: string;
  phone_number: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string(),
  phone_number: Yup.string().required("Phone number is required"),
});

export const LeadDetailsForm = ({
  businessId,
  initialData,
}: LeadDetailsFormProps) => {
  const navigate = useNavigate();
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { mutate: upsertCustomer } = useUpsertCustomer(businessId);

  const {
    id: leadId,
    name,
    phone_number: phoneNumber,
    lead_referral: leadReferral,
    created_at: createdAt,
  } = initialData || {};

  const initialValues = useMemo<CustomerFormValues>(
    () => ({
      name: name || "",
      phone_number: formatPhoneNumber(phoneNumber) || "",
    }),
    [initialData],
  );

  const handleSubmit = useCallback(
    async (
      values: CustomerFormValues,
      { resetForm }: FormikHelpers<CustomerFormValues>,
    ) =>
      upsertCustomer(
        {
          ...values,
          id: leadId,
          status: CUSTOMER_STATUS.POTENTIAL,
        },
        {
          onSuccess: () => {
            resetForm({ values });

            updateSidebar({
              selectedItemId: initialData?.id,
            });

            toast.success("Customer updated successfully");
          },
          onError: (error) => {
            toast.error("Failed to update advocate", {
              description:
                error instanceof Error ? error.message : "Please try again",
            });
          },
        },
      ),
    [updateSidebar, upsertCustomer, leadId, initialData],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      className="h-full"
    >
      {({ values, setFieldValue, getFieldProps, dirty, resetForm }) => (
        <Form className="w-full">
          <Stack className="w-full" align="start" gap={6}>
            <FormInput
              label="Name"
              placeholder="Enter name"
              fullWidth
              {...getFieldProps("name")}
            />

            <PhoneInput
              name="phone_number"
              value={values.phone_number}
              onChange={(value) => setFieldValue("phone_number", value)}
              disabled
              required
              fullWidth
              disabledTooltip={
                <>
                  {" "}
                  Contact support at{" "}
                  <span className="font-semibold">support@volca.ai</span> to
                  change a customer phone number
                </>
              }
            />

            {!!leadReferral?.service_needed && (
              <InfoWithLabel label="Service needed">
                {leadReferral.service_needed}
              </InfoWithLabel>
            )}

            {createdAt && (
              <InfoWithLabel label="Date created">
                {formatDate(createdAt)}
              </InfoWithLabel>
            )}

            {!!leadReferral?.advocate?.name && (
              <Item
                render={<button type="button" />}
                className="p-0 text-left"
                onClick={() => {
                  navigate(
                    `/business-settings/${businessId}/customers/advocates`,
                  );
                  updateSidebar({
                    section: SIDEBAR_SECTIONS.advocates,
                    mode: SIDEBAR_MODES.view,
                    selectedItemId: leadReferral?.advocate?.id || undefined,
                    isOpen: true,
                  });
                }}
              >
                <ItemContent>
                  <InfoWithLabel label="Referred by">
                    {leadReferral.advocate.name}
                  </InfoWithLabel>
                </ItemContent>
                <ItemActions>
                  <CaretRightIcon className="h-5 w-5" />
                </ItemActions>
              </Item>
            )}

            <Stack align="start" gap={4} direction="row">
              <Button type="submit" size="sm" disabled={!dirty}>
                Save changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!dirty}
                onClick={() => resetForm({ values: initialValues })}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
